import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, UserPlus } from "lucide-react";
import {
  useProjectAssignments,
  useCreateAssignment,
  useDeleteAssignment,
} from "@/hooks/useAssignments";
import { useRoster } from "@/hooks/useRoster";
import { cn } from "@/lib/cn";
import { avatarTone } from "@/lib/format";

const ROLE_LABELS: Record<string, string> = {
  pm: "PM", ba: "BA", functional: "Functional", technical: "Technical",
  developer: "Developer", infrastructure: "Infrastructure", dba: "DBA",
  erp: "ERP", operations: "Operations", "wms consultant": "WMS Consultant",
};

interface Props {
  projectId: string;
}

export function ProjectAssignments({ projectId }: Props) {
  const { data: assignments, isLoading } = useProjectAssignments(projectId);
  const { data: rosterData } = useRoster();
  const createAssignment = useCreateAssignment();
  const deleteAssignment = useDeleteAssignment();

  const [adding, setAdding] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  // Capacity primitive (post-May-2026): explicit hours/week per
  // assignment. Default to 0 — the user explicitly sets the hours.
  // Old % allocation field is no longer the source of truth; we
  // still write allocation_pct=1.0 so any back-compat consumer
  // doesn't break.
  const [newHrs, setNewHrs] = useState("0");
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editHrs, setEditHrs] = useState("");
  // Clicking the name opens the per-person drill-in (their total
  // assignments across everything they're on, with capacity).
  const navigate = useNavigate();

  const roster = Array.isArray(rosterData) ? rosterData : [];
  const list = Array.isArray(assignments) ? assignments : [];

  // Build set of already-assigned person+role combos
  const assignedSet = new Set(list.map((a) => `${a.person_name}::${a.role_key}`));

  const handleAdd = () => {
    if (!selectedPerson || !selectedRole) return;
    const hrs = Math.max(0, parseFloat(newHrs) || 0);
    createAssignment.mutate(
      {
        projectId,
        person_name: selectedPerson,
        role_key: selectedRole,
        // allocation_pct stays at 1.0 (back-compat default). The
        // capacity engine reads hours_per_week, so this is what
        // actually matters going forward.
        allocation_pct: 1.0,
        hours_per_week: hrs,
      },
      {
        onSuccess: () => {
          setAdding(false);
          setSelectedPerson("");
          setSelectedRole("");
          setNewHrs("0");
        },
      },
    );
  };

  const handleRemove = (personName: string, roleKey: string) => {
    deleteAssignment.mutate({ projectId, person_name: personName, role_key: roleKey });
  };

  // When a person is selected, auto-fill their role
  const handlePersonSelect = (name: string) => {
    setSelectedPerson(name);
    const member = roster.find((m) => m.name === name);
    if (member) {
      setSelectedRole(member.role_key);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Team Assignments
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 text-[11px] font-medium text-brand-navy-600 hover:text-brand-navy-800 dark:hover:text-brand-navy-300 transition-colors"
          >
            <UserPlus className="h-3 w-3" />
            Assign person
          </button>
        )}
      </div>

      {/* Existing assignments */}
      {isLoading && (
        <div className="text-xs text-ink-subtle py-2">Loading assignments...</div>
      )}
      {list.length > 0 && (
        <div className="space-y-1 mb-2">
          {list.map((a) => {
            const key = `${a.person_name}::${a.role_key}`;
            const isEditing = editingKey === key;
            return (
              <div
                key={key}
                className="flex items-center gap-2 rounded-md border border-line-soft bg-canvas px-3 py-1.5"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/people/${encodeURIComponent(a.person_name)}`)}
                  className="flex items-center gap-2 flex-1 min-w-0 -my-1 -ml-1 rounded-md px-1 py-1 text-left hover:bg-surface transition-colors"
                  title="See everything this person is assigned to"
                >
                  <div
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold"
                    style={{ backgroundColor: avatarTone(a.person_name) }}
                  >
                    {a.person_name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                  </div>
                  <span className="text-sm font-medium text-ink truncate">
                    {a.person_name}
                  </span>
                </button>
                <span className="text-[11px] text-ink-muted">
                  {ROLE_LABELS[a.role_key] ?? a.role_key}
                </span>
                {isEditing ? (
                  <span className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={editHrs}
                      onChange={(e) => setEditHrs(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          (e.target as HTMLInputElement).blur();
                        }
                        if (e.key === "Escape") setEditingKey(null);
                      }}
                      onBlur={() => {
                        // Save on blur so clicking the arrows then
                        // anywhere else persists, no Save click needed.
                        const hrs = Math.max(0, parseFloat(editHrs) || 0);
                        createAssignment.mutate({
                          projectId,
                          person_name: a.person_name,
                          role_key: a.role_key,
                          allocation_pct: a.allocation_pct ?? 1.0,
                          hours_per_week: hrs,
                        }, { onSuccess: () => setEditingKey(null) });
                      }}
                      autoFocus
                      className="w-20 rounded border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-1.5 py-0.5 text-center text-[11px] tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
                    />
                    <span className="text-[10px] text-ink-muted">h/wk</span>
                  </span>
                ) : (
                  <button
                    onClick={() => {
                      setEditingKey(key);
                      setEditHrs(
                        a.hours_per_week != null
                          ? a.hours_per_week.toString()
                          : "0",
                      );
                    }}
                    className="text-[11px] font-medium tabular-nums text-ink-2 w-20 text-right hover:text-brand-navy-600 dark:hover:text-brand-navy-400 hover:underline transition-colors cursor-pointer"
                    title="Click to edit weekly hours"
                  >
                    {a.hours_per_week != null
                      ? `${a.hours_per_week.toFixed(1)}h/wk`
                      : <span className="text-amber-600 dark:text-amber-400">not set</span>}
                  </button>
                )}
                <button
                  onClick={() => handleRemove(a.person_name, a.role_key)}
                  className="rounded p-0.5 text-ink-faint hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {!isLoading && list.length === 0 && !adding && (
        <div className="text-xs text-ink-subtle py-2">
          No assignments yet. Add a person and the hours/week they spend on this project.
        </div>
      )}

      {/* Add form */}
      {adding && (
        <div className="rounded-md border border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50/30 dark:bg-brand-navy-500/10 p-3 space-y-2">
          <div className="space-y-2">
            <select
              value={selectedPerson}
              onChange={(e) => handlePersonSelect(e.target.value)}
              className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
            >
              <option value="">Select person...</option>
              {roster
                .filter((m) => !assignedSet.has(`${m.name}::${m.role_key}`))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((m) => (
                  <option key={m.name} value={m.name}>
                    {m.name} ({ROLE_LABELS[m.role_key] ?? m.role_key})
                  </option>
                ))}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
              >
                <option value="">Role...</option>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={newHrs}
                  onChange={(e) => setNewHrs(e.target.value)}
                  className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-center tabular-nums focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
                  placeholder="Hours/week"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-ink-subtle">h/wk</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setAdding(false)}
              className="rounded px-2 py-1 text-xs font-medium text-ink-muted hover:bg-raised transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!selectedPerson || !selectedRole || createAssignment.isPending}
              className="flex items-center gap-1 rounded bg-brand-navy-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
            >
              <Plus className="h-3 w-3" />
              {createAssignment.isPending ? "Adding..." : "Add"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
